import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, setSessionExpiredCallback } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await authApi.me();
      setUser(data);
    } catch {
      // Expected: user not authenticated — not an error, just means guest state
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Register session-expired callback so the interceptor can clear user state
  useEffect(() => {
    setSessionExpiredCallback(() => {
      setUser(null);
    });
    return () => setSessionExpiredCallback(null);
  }, []);

  const login = async (email, password) => {
    const { data } = await authApi.login({ email, password });
    setUser(data);
    return data;
  };

  const register = async (payload, legacyPassword, legacyName) => {
    // Backwards-compatible signature:
    //   * New code:  register({ email, password, firstName, lastName })
    //   * Legacy:    register(email, password, name)
    // Old call sites (if any remain) keep working without changes.
    const data = typeof payload === 'string'
      ? { email: payload, password: legacyPassword, name: legacyName }
      : payload;
    const { data: resp } = await authApi.register(data);
    setUser(resp);
    return resp;
  };

  const logout = async () => {
    try { await authApi.logout(); } catch { /* Intentional: logout errors must not prevent state clear */ }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
